import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, Row, Table } from 'react-bootstrap';
import { FaChartLine, FaEuroSign, FaTimes } from 'react-icons/fa';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ErrorToast } from '../../../utils/toasters';
import { ControllingCard } from './ControllingCard';
import { AgencyRevenueCampaignsFilter } from './AgencyRevenueCampaignsFilter';
import { ContollingService } from '../ControllingService';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import TooltipOverlay from '../../../shared/components/TooltipOverlay';
import { IAgencyRevenueCampaignsStatistics, ICampaign } from '../../../model/controlling/Controlling';
import { EditableNumberCell } from '../../../shared/components/EditableNumberCell';
import { CampaignsService } from '../../campaigns/CampaignsService';
import { ITableColumn } from '../../../shared/components/TableSkeleton';

export interface IFiltersAgencyRevenueCampaigns {
    page: number;
    perPage: number;
    id: string;
    managerId: string | undefined;
    campaignId: string | undefined;
    clientId: string | undefined;
    totalPages: number;
    year: number;
}
const AGENCY_REVENUE_COLUMNS: ITableColumn[] = [
    { label: 'Campaign name' },
    { label: 'Client name' },
    { label: 'Campaign managers' },
    { label: 'Plan budget' },
    { label: 'Influencer cost' },
    { label: 'Additional cost' },
    { label: 'Margin' },
    { label: 'Margin %' },
];
export function AgencyRevenueCampaigns() {
    const currentYear = new Date().getFullYear();
    const [dataLoading, setDataLoading] = useState(true);
    const [campaigns, setCampaigns] = useState<ICampaign[]>([]);
    const [statistics, setStatistics] = useState<IAgencyRevenueCampaignsStatistics | null>(null);
    const [filters, setFilters] = useState<IFiltersAgencyRevenueCampaigns>({
        page: 1,
        perPage: 15,
        id: '',
        managerId: '',
        campaignId: '',
        clientId: '',
        totalPages: 0,
        year: currentYear,
    });

    const [statsLoading, setStatsLoading] = useState(true);
    const requestIdRef = React.useRef(0);

    const fetchCampaigns = (params: IFiltersAgencyRevenueCampaigns, pageOverride?: number) => {
        const requestId = ++requestIdRef.current;
        setDataLoading(true);
        if (pageOverride === undefined) setStatsLoading(true);
        const page = pageOverride ?? params.page;

        ContollingService.getAgencyRevenueCampaigns({ ...params, page })
            .then(({ data, page: responsePage, count, info }: any) => {
                if (requestId !== requestIdRef.current) return;

                const totalPages = Math.ceil(count / params.perPage);

                if (totalPages > 0 && page > totalPages) {
                    fetchCampaigns({ ...params, page: 1 });
                    return;
                }

                setCampaigns(data);
                setStatistics(info ?? null);

                setFilters({
                    ...params,
                    page: responsePage,
                    totalPages,
                });
            })
            .catch(err => {
                if (requestId === requestIdRef.current) ErrorToast(err);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) {
                    setDataLoading(false);
                    setStatsLoading(false);
                }
            });
    };

    const handlePageChange = (page: number) => {
        fetchCampaigns(filters, page);
    };
    useEffect(() => {
        fetchCampaigns(filters);
    }, []);
    return (
        <>
            <div className="mb-5">
                <Row className="gap-y-4">
                    <ControllingCard
                        cardName="Total Revenue"
                        icon={<FaEuroSign />}
                        baseColor="primary"
                        total={statistics?.revenue ?? 0}
                        styleCard="h-100"
                        isLoading={statsLoading}
                    />
                    <ControllingCard
                        cardName="Total Margin"
                        icon={<FaChartLine />}
                        baseColor="primary"
                        total={statistics?.margin ?? 0}
                        styleCard="h-100"
                        isLoading={statsLoading}
                    />
                </Row>
            </div>

            <Card className="mb-5 p-4">
                <AgencyRevenueCampaignsFilter
                    handleState={fetchCampaigns}
                    setState={setFilters}
                    offersFilter={filters}
                />
            </Card>

            <TableLayout
                pagination={filters}
                listLength={campaigns.length}
                handlePages={handlePageChange}
                isLoading={dataLoading}
                dataName={'campaigns'}
                skeletonColumns={AGENCY_REVENUE_COLUMNS}
                skeletonRows={filters.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {AGENCY_REVENUE_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {campaigns.map(campaign => (
                            <tr key={campaign.campaignId}>
                                <td>
                                    {campaign.campaignName ? (
                                        <>
                                            <span className="text-primary">({campaign.campaignId})</span>{' '}
                                            {campaign.campaignName}
                                        </>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {campaign.clientCompanies?.length ? (
                                        <div className="d-flex align-items-center">
                                            {campaign.clientCompanies.slice(0, 3).map(client => {
                                                const fullName = `${client.firstName ?? ''} ${
                                                    client.lastName ?? ''
                                                }`.trim();
                                                const name = client.displayName || fullName;

                                                return (
                                                    <TooltipOverlay
                                                        key={client.id}
                                                        content={name}
                                                        style={{ marginRight: '6px' }}
                                                    >
                                                        <img
                                                            src={
                                                                client.profilePhoto?.thumbnail ??
                                                                '/icons/UserPlaceholder.png'
                                                            }
                                                            className="campaign-card-img"
                                                            alt={name}
                                                            onError={e => {
                                                                (e.target as HTMLImageElement).onerror = null;
                                                                (e.target as HTMLImageElement).src =
                                                                    '/icons/UserPlaceholder.png';
                                                            }}
                                                        />
                                                    </TooltipOverlay>
                                                );
                                            })}

                                            {campaign.clientCompanies.length > 3 && (
                                                <TooltipOverlay
                                                    content={
                                                        <div className="d-flex flex-column">
                                                            {campaign.clientCompanies.slice(3).map(client => {
                                                                const fullName = `${client.firstName ?? ''} ${
                                                                    client.lastName ?? ''
                                                                }`.trim();
                                                                const name = client.displayName || fullName;

                                                                return (
                                                                    <div
                                                                        key={client.id}
                                                                        className="d-flex align-items-center mb-1"
                                                                    >
                                                                        <img
                                                                            src={
                                                                                client.profilePhoto?.thumbnail ??
                                                                                '/icons/UserPlaceholder.png'
                                                                            }
                                                                            className="campaign-card-img me-2"
                                                                            alt={name}
                                                                            onError={e => {
                                                                                (e.target as HTMLImageElement).onerror =
                                                                                    null;
                                                                                (e.target as HTMLImageElement).src =
                                                                                    '/icons/UserPlaceholder.png';
                                                                            }}
                                                                        />
                                                                        <span>{name}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    }
                                                >
                                                    <span className="ms-1 cursor-pointer">
                                                        +{campaign.clientCompanies.length - 3}
                                                    </span>
                                                </TooltipOverlay>
                                            )}
                                        </div>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td>
                                    {campaign.assignedManagers?.length ? (
                                        <div className="d-flex align-items-center">
                                            {campaign.assignedManagers.slice(0, 3).map(manager => {
                                                const fullName = `${manager.firstName ?? ''} ${
                                                    manager.lastName ?? ''
                                                }`.trim();
                                                const name = manager.displayName || fullName;

                                                return (
                                                    <TooltipOverlay
                                                        key={manager.id}
                                                        content={name}
                                                        style={{ marginRight: '6px' }}
                                                    >
                                                        <img
                                                            src={
                                                                manager.profilePhoto?.thumbnail ??
                                                                '/icons/UserPlaceholder.png'
                                                            }
                                                            className="campaign-card-img"
                                                            alt={name}
                                                            onError={e => {
                                                                (e.target as HTMLImageElement).onerror = null;
                                                                (e.target as HTMLImageElement).src =
                                                                    '/icons/UserPlaceholder.png';
                                                            }}
                                                        />
                                                    </TooltipOverlay>
                                                );
                                            })}

                                            {campaign.assignedManagers.length > 3 && (
                                                <TooltipOverlay
                                                    content={
                                                        <div className="d-flex flex-column">
                                                            {campaign.assignedManagers.slice(3).map(manager => {
                                                                const fullName = `${manager.firstName ?? ''} ${
                                                                    manager.lastName ?? ''
                                                                }`.trim();
                                                                const name = manager.displayName || fullName;

                                                                return (
                                                                    <div
                                                                        key={manager.id}
                                                                        className="d-flex align-items-center mb-1"
                                                                    >
                                                                        <img
                                                                            src={
                                                                                manager.profilePhoto?.thumbnail ??
                                                                                '/icons/UserPlaceholder.png'
                                                                            }
                                                                            className="campaign-card-img me-2"
                                                                            alt={name}
                                                                            onError={e => {
                                                                                (e.target as HTMLImageElement).onerror =
                                                                                    null;
                                                                                (e.target as HTMLImageElement).src =
                                                                                    '/icons/UserPlaceholder.png';
                                                                            }}
                                                                        />
                                                                        <span>{name}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    }
                                                >
                                                    <span className="ms-1 cursor-pointer">
                                                        +{campaign.assignedManagers.length - 3}
                                                    </span>
                                                </TooltipOverlay>
                                            )}
                                        </div>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td>{priceSplitter(campaign.grossBudget)} €</td>
                                <td>{priceSplitter(campaign.revenueWithFee)} €</td>
                                <td>
                                    <EditableNumberCell
                                        value={campaign.additionalCosts}
                                        onSave={newVal => {
                                            setCampaigns(prev =>
                                                prev.map(item =>
                                                    item.campaignId === campaign.campaignId
                                                        ? { ...item, additionalCosts: newVal }
                                                        : item
                                                )
                                            );

                                            CampaignsService.updateAdditionalCosts(Number(campaign.campaignId), {
                                                additionalCosts: newVal,
                                            }).catch(err => ErrorToast(err));
                                        }}
                                    />
                                </td>{' '}
                                {/*<td>{priceSplitter(campaign.additionalCosts)} €</td>*/}
                                <td>{priceSplitter(campaign.margin)} €</td>
                                <td>{priceSplitter(campaign.marginPercentage)} %</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
